const readMapKey = (userId) => `message-thread-read-map:${String(userId || '')}`;

const parseTimestamp = (value) => {
  const ts = value ? Date.parse(value) : NaN;
  return Number.isFinite(ts) ? ts : Date.now();
};

export const hasThreadReadMap = (userId) => {
  if (!userId) return false;
  return localStorage.getItem(readMapKey(userId)) !== null;
};

export const getThreadReadMap = (userId) => {
  if (!userId) return {};
  try {
    const raw = localStorage.getItem(readMapKey(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (err) {
    return {};
  }
};

export const setThreadReadMap = (userId, map) => {
  if (!userId) return;
  localStorage.setItem(readMapKey(userId), JSON.stringify(map || {}));
  window.dispatchEvent(new Event('message-read-updated'));
};

export const buildReadMapFromThreads = (threads = []) =>
  threads.reduce((acc, thread) => {
    if (thread?.id) {
      acc[thread.id] = parseTimestamp(thread.updated_at);
    }
    return acc;
  }, {});

export const markThreadRead = (userId, threadId, updatedAt, existingMap = null) => {
  if (!userId || !threadId) return existingMap || {};
  const base = existingMap || getThreadReadMap(userId);
  const next = {
    ...base,
    [threadId]: parseTimestamp(updatedAt),
  };
  setThreadReadMap(userId, next);
  return next;
};

export const isThreadUnread = (thread, readMap) => {
  if (!thread?.id) return false;
  const threadUpdatedAt = parseTimestamp(thread.updated_at);
  const readAt = Number(readMap?.[thread.id] || 0);
  return threadUpdatedAt > readAt;
};

/** True when the API returned per-thread read state (after migration 011). */
export const threadListHasServerUnread = (threads) =>
  Array.isArray(threads) && threads.some((thread) => typeof thread?.is_unread === 'boolean');

/** Prefer server `is_unread`; fall back to local read map before deploy / offline. */
export const isThreadUnreadForViewer = (thread, readMap) => {
  if (typeof thread?.is_unread === 'boolean') return thread.is_unread;
  return isThreadUnread(thread, readMap);
};

export const countUnreadThreads = (threads, readMap) =>
  (threads || []).reduce(
    (acc, thread) => (isThreadUnreadForViewer(thread, readMap) ? acc + 1 : acc),
    0
  );

/** Local read map + POST /message-threads/{id}/read (no-op if API unavailable). */
export const syncThreadReadWithServer = async (
  userId,
  threadId,
  updatedAt,
  { ownerId, existingMap = null } = {}
) => {
  const next = markThreadRead(userId, threadId, updatedAt, existingMap);
  try {
    const { default: messageThreadsApiService } = await import('../services/messageThreadsApi');
    await messageThreadsApiService.markThreadRead(threadId, {
      userId,
      ownerId: ownerId || userId,
    });
  } catch {
    // Keep local cursor; server sync requires migration 011 + API deploy.
  }
  return next;
};
